import type {
  CloudSecurityTreeDirection,
  DeviceSecurityResponse,
} from '@ipfabric/utils';
import { useQuery } from '@tanstack/react-query';
import { AnimationClose } from 'components/AnimationClose/AnimationClose';
import { ModalFullContent } from 'components/ModalFullContent/ModalFullContent';
import { NothingToSeeHere } from 'components/NothingToSeeHere/NothingToSeeHere';
import { SecuritySchema } from 'components/SecuritySchema/SecuritySchema';
import type { SecuritySchemaDataSource } from 'components/SecuritySchema/model/SecuritySchemaDataSource';
import type { RuleChainScope } from 'components/SecuritySchema/model/SecuritySchemaScope';
import { LoaderAndText } from 'containers/LoaderAndText/LoaderAndText';
import type { FC, ReactNode } from 'react';
import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { createUrl } from 'utils/createUrl';
import { useSnapshotId } from 'utils/hooks/useSnapshotId';
import { useApi } from 'utils/request/useApi';

interface CloudSecurityTreeModalState {
  resourceName: string;
  direction: CloudSecurityTreeDirection;
}

/**
 * In-place overlay of a cloud security policy's tree, opened from a table cell
 * via `location.state` (a `cloudSecurityTree` column link, see
 * `createTableColumnLinkFromOas`) so the table underneath stays mounted with
 * its filters. Closing clears the state.
 */
export const useCloudSecurityTreeModal = (): ReactNode => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as
    | { cloudSecurityTreeModal?: CloudSecurityTreeModalState }
    | null
    | undefined;
  const modal = state?.cloudSecurityTreeModal;

  const handleClose = () => {
    navigate(location.pathname, { state: null });
  };

  return modal ? (
    <CloudSecurityTreeModal
      resourceName={modal.resourceName}
      direction={modal.direction}
      onClose={handleClose}
    />
  ) : null;
};

interface Props extends CloudSecurityTreeModalState {
  onClose: () => void;
}

const CloudSecurityTreeModal: FC<Props> = ({
  resourceName,
  direction,
  onClose,
}) => {
  const snapshotId = useSnapshotId();
  const api = useApi();

  const { data, isPending } = useQuery({
    ...api.query<DeviceSecurityResponse>({
      method: 'GET',
      url: createUrl('/security/cloud/policy', {
        resourceName,
        direction,
        snapshot: snapshotId ?? '',
      }),
    }),
    enabled: snapshotId !== null,
  });

  const entryPoint: RuleChainScope | null = useMemo(() => {
    const entryChainName = data?.security?.machineCloud?.entryChainName;
    return entryChainName
      ? { type: 'ruleChain', name: entryChainName, securityType: 'cloud' }
      : null;
  }, [data]);

  const dataSource: SecuritySchemaDataSource | undefined = useMemo(
    () => (data ? { data } : undefined),
    [data],
  );

  if (isPending) {
    return (
      <ModalFullContent>
        <div className='flex flex-1 flex-col bg-white'>
          <div className='flex flex-row justify-end p-5'>
            <AnimationClose onClick={onClose} />
          </div>
          <LoaderAndText />
        </div>
      </ModalFullContent>
    );
  }

  if (!entryPoint || !dataSource) {
    return (
      <ModalFullContent>
        <div className='flex flex-1 flex-col bg-white'>
          <div className='flex flex-row justify-end p-5'>
            <AnimationClose onClick={onClose} />
          </div>
          <NothingToSeeHere className='flex flex-1 items-center justify-center' />
        </div>
      </ModalFullContent>
    );
  }

  return (
    <ModalFullContent>
      <div className='flex flex-1 flex-col bg-white'>
        <SecuritySchema
          breadcrumbsAddon={<AnimationClose onClick={onClose} />}
          className='flex-1'
          entryPoint={entryPoint}
          dataSource={dataSource}
        />
      </div>
    </ModalFullContent>
  );
};
